import clsx from "clsx";
import React, { useRef, useState } from "react";
import { Controller, useFieldArray, useFormContext } from "react-hook-form";
import { useOnClickOutside } from "usehooks-ts";

import { INPUT_SIZES, INPUT_VARIANTS } from "../../utils/constants";
import { Chip } from "../ui/Chip";
import { CancelButton, FormError } from "./shared";

import { IoIosArrowDown } from "react-icons/io";
import { LuPlus } from "react-icons/lu";

const MAX_CHIPS_COUNT = 3

interface StaticInputProps {
    label?: string;
    description?: string;
    values: string[];
    handleChange: (newValues: string[]) => void;
    disabled?: boolean;
    inputSize?: keyof typeof INPUT_SIZES;
    inputVariant?: keyof typeof INPUT_VARIANTS;
    maxChipsCount?: number;
}

export const StaticInput: React.FC<StaticInputProps> = ({
    label = "Keywords are separated by ';', Hit Enter to add",
    description,
    values,
    handleChange,
    disabled = false,
    inputSize = "fit",
    inputVariant = "gray_text_white_bg",
    maxChipsCount = MAX_CHIPS_COUNT
}) => {
    const dropdownRef = useRef<HTMLDivElement>(null);
    const [inputValue, setInputValue] = useState("");
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);

    const toggleDropdown = () => setIsDropdownOpen((prev) => !prev);

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {

        if ((e.key === 'Enter')) {
            e.preventDefault();
            handleInsert();
        }
    };

    const handleInsert = () => {
        const trimmed = inputValue.trim().split(';');

        if (trimmed.length > 0) {
            const set = new Set([...values, ...trimmed]);
            handleChange(Array.from(set));
        }

        setInputValue('');
    }

    const removeKeyword = (slug: string) => {
        handleChange(values.filter((v) => v !== slug));
    };

    const renderValue = (value: string) => {
        const isOptionSelected = values.includes(value);

        return (
            <div
                key={value}
                className={clsx(
                    "flex justify-between items-center",
                    "w-full cursor-pointer block px-4 py-1 my-1 text-sm rounded",
                )}
                role="menuitem"
            >
                <span>
                    {value}
                </span>

                {isOptionSelected && (
                    <CancelButton
                        size={20}
                        onClick={() => removeKeyword(value)}
                    />
                )}
            </div>
        )
    }

    useOnClickOutside(dropdownRef as React.RefObject<HTMLElement>, () => setIsDropdownOpen(false));

    return (
        <div
            className={clsx(
                isDropdownOpen && "z-50",
                "relative"
            )}
        >
            {description && <p className="text-sm font-semibold text-cs-700 mb-1">{description}</p>}

            <div
                className={clsx(
                    "h-10 flex flex-wrap items-center px-2 py-1 border-[0.5px] text-sm rounded shadow cursor-text",
                    INPUT_SIZES[inputSize],
                    INPUT_VARIANTS[inputVariant],
                    INPUT_VARIANTS['common']
                )}
                onClick={() => setIsDropdownOpen(true)}
            >
                <input
                    type="text"
                    value={inputValue}
                    onChange={(e) => setInputValue(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder={label}
                    className="grow outline-none bg-transparent p-1"
                    disabled={disabled}
                />

                {values.length > 0 && (
                    <div className="me-2 flex items-center gap-1">
                        {values.slice(0, maxChipsCount).map((value) => {
                            return (
                                <Chip
                                    key={value}
                                    rightIcon={
                                        <CancelButton
                                            size={18}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                removeKeyword(value);
                                            }}
                                        />
                                    }
                                >
                                    {value}
                                </Chip>
                            );
                        })}

                        {values.length > maxChipsCount && (
                            <span className='text-cs-normal-blue'>
                                +{values.length - maxChipsCount}
                            </span>
                        )}
                    </div>
                )}

                <LuPlus
                    size={18}
                    className={clsx(
                        'cursor-pointer fill-current',
                        // 'ml-2 pl-2 border-l'
                    )}
                    onClick={handleInsert}
                />

                <IoIosArrowDown
                    size={18}
                    className={clsx(
                        'cursor-pointer fill-current',
                        // 'ml-2 pl-2 border-l'
                    )}
                    onClick={toggleDropdown}
                />
            </div>

            {(isDropdownOpen && values.length > 0) && (
                <div
                    className={clsx(
                        "max-h-48 min-w-56 w-full overflow-y-scroll",
                        "origin-center sm:origin-top-right md:right-0",
                        "absolute mt-2 rounded shadow bg-white ring-1 ring-black ring-opacity-5",
                    )}
                    ref={dropdownRef}
                    role="menu"
                    aria-orientation="vertical"
                    aria-labelledby="dropdown-button"
                >
                    {values.map((value) => (
                        <React.Fragment key={value}>
                            {renderValue(value)}
                        </React.Fragment>
                    ))}
                </div>
            )}
        </div>
    );
};

interface ControlledStaticInputProps extends Omit<StaticInputProps, "values" | "handleChange"> {
    name: string;
}

export const ControlledStaticInput: React.FC<ControlledStaticInputProps> = (props) => {
    const {
        name,
        ...restOfProps
    } = props;

    const formContext = useFormContext();

    const { control, formState } = formContext;

    const {
        replace
    } = useFieldArray({
        control,
        name
    })

    const handleChange = (value: string[]) => {
        replace(value)
    }

    return (
        <div className='flex flex-col justify-start'>
            <Controller
                name={name}
                control={control}
                render={({ field: { ref, ...restOfField } }) => {
                    return (
                        <StaticInput
                            {...restOfField}
                            {...restOfProps}
                            handleChange={handleChange}
                            values={restOfField.value}
                        />
                    )
                }}
            />

            <FormError error={formState?.errors?.[name]?.message as string} />
        </div>
    )
}